import { css } from 'styled-components'

import {
  BackgroundProps,
  BorderRadiusProps,
  ColorProps,
  ElevationProps,
  FlexProps,
  FontSizeProps,
  FontWeightProps,
  PositionProps,
  SizeProps,
  SpacingProps,
} from '@/@types/decorators'

export const withColor =
  (defaultValue = 'inherit') =>
  ({ color, theme }: ColorProps) => css`
    color: ${color ? theme.colors[color] || color : defaultValue};
  `

export const withBackgroundColor =
  (defaultValue: string = 'inherit') =>
  ({ backgroundColor, theme }: BackgroundProps) => {
    return css`
      background-color: ${theme.colors[backgroundColor || defaultValue] ||
      backgroundColor ||
      theme.colors[defaultValue] ||
      defaultValue};
    `
  }

export const withBackgroundGradient =
  (defaultValue = 'inherit') =>
  ({ backgroundGradient, theme }: BackgroundProps) => {
    return (
      backgroundGradient &&
      css`
        background: ${theme.gradients[backgroundGradient] ||
        theme.gradients[defaultValue] ||
        theme.colors[defaultValue] ||
        defaultValue};
      `
    )
  }

export const withWidth =
  (defaultValue = 'auto') =>
  ({ width }: SizeProps) => {
    return css`
      width: ${width || defaultValue};
    `
  }

export const withMinWidth =
  (defaultValue = 'none') =>
  ({ minWidth }: SizeProps) => {
    return css`
      min-width: ${minWidth || defaultValue};
    `
  }

export const withMaxWidth =
  (defaultValue = 'none') =>
  ({ maxWidth }: SizeProps) => {
    return css`
      max-width: ${maxWidth || defaultValue};
    `
  }

export const withHeight =
  (defaultValue = 'auto') =>
  ({ height, theme }: SizeProps) => {
    return css`
      height: ${theme.spacing[height || defaultValue] || defaultValue};
    `
  }

export const withMinHeight =
  (defaultValue = 'none') =>
  ({ minHeight }: SizeProps) => {
    return css`
      min-height: ${minHeight || defaultValue};
    `
  }

export const withMaxHeight =
  (defaultValue = 'none') =>
  ({ maxHeight }: SizeProps) => {
    return css`
      max-height: ${maxHeight || defaultValue};
    `
  }

export const withMargin =
  (defaultValue = '0px') =>
  ({ margin }: SpacingProps) => {
    return css`
      margin: ${margin || defaultValue};
    `
  }

export const withPadding =
  (defaultValue = '0px') =>
  ({ padding, theme }: SpacingProps) => {
    const finalPadding = padding
      ? theme.spacing[padding] || padding
      : defaultValue
    return css`
      padding: ${finalPadding};
    `
  }

export const withBorderRadius =
  (defaultValue = '0px') =>
  ({ borderRadius, theme }: BorderRadiusProps) => {
    return css`
      border-radius: ${theme.borderRadius[borderRadius || defaultValue] ||
      borderRadius ||
      theme.borderRadius[defaultValue] ||
      defaultValue};
    `
  }

export const withElevation =
  (defaultValue = 'none') =>
  ({ elevation, theme }: ElevationProps) => {
    return css`
      box-shadow: ${theme.boxShadows[elevation || defaultValue] ||
      theme.boxShadows[defaultValue] ||
      defaultValue};
    `
  }

export const withFlex =
  (defaultValues: FlexProps = {}) =>
  ({
    flex,
    flexWrap,
    flexDirection,
    alignItems,
    alignSelf,
    justifyContent,
  }: FlexProps) => css`
    display: flex;
    ${flex &&
    css`
      flex: ${flex};
    `}
    flex-wrap: ${flexWrap || defaultValues.flexWrap || 'nowrap'};
    flex-direction: ${flexDirection || defaultValues.flexDirection || 'column'};
    align-items: ${alignItems || defaultValues.alignItems || 'flex-start'};
    align-self: ${alignSelf || defaultValues.alignSelf || 'auto'};
    justify-content: ${justifyContent ||
    defaultValues.justifyContent ||
    'flex-start'};
  `

export const withFontSize =
  (defaultValue = 'medium') =>
  ({ fontSize, theme }: FontSizeProps) => {
    return css`
      font-size: ${theme.fontSizes[fontSize || defaultValue] ||
      fontSize ||
      theme.fontSizes[defaultValue] ||
      defaultValue};
    `
  }

export const withFontWeight =
  (defaultValue = 'normal') =>
  ({ fontWeight, theme }: FontWeightProps) => {
    return css`
      font-weight: ${theme.fontWeights[fontWeight || defaultValue] ||
      fontWeight ||
      theme.fontWeights[defaultValue] ||
      defaultValue};
    `
  }

export const withPosition =
  (defaultValue = 'static') =>
  ({ position }: PositionProps) => {
    return css`
      position: ${position || defaultValue};
    `
  }

export const withGap =
  (defaultValue = '8px') =>
  ({ gap, theme }: SpacingProps) => {
    return css`
      gap: ${theme.spacing[gap || defaultValue] || defaultValue};
    `
  }
